import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function generateItineraryPDF(element, logoUrl, fileName = 'itinerary.pdf') {
  // Preserve original styles
  const els = Array.from(element.querySelectorAll('p, li, h3, h2'));
  const originalStyles = els.map(el => ({ el, marginBottom: el.style.marginBottom, lineHeight: el.style.lineHeight }));
  els.forEach(el => { el.style.marginBottom = '4px'; el.style.lineHeight = '1.2'; });

  // Setup PDF
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  const headerY = 30;

  // Temporarily adjust element for canvas capture
  const originalWidth = element.style.width;
  const originalFontSize = element.style.fontSize;
  const originalWhiteSpace = element.style.whiteSpace;
  const originalLetterSpacing = element.style.letterSpacing;
  element.style.width = `${pageW - margin * 2}px`;
  element.style.fontSize = '0.65em';
  element.style.whiteSpace = 'pre-wrap';
  element.style.letterSpacing = '0.05em';

  // Capture canvas
  const canvas = await html2canvas(element, { scale: window.devicePixelRatio || 2, useCORS: true, logging: false });

  // Restore element styles
  element.style.width = originalWidth;
  element.style.fontSize = originalFontSize;
  element.style.whiteSpace = originalWhiteSpace;
  element.style.letterSpacing = originalLetterSpacing;

  // Preload watermark logo
  const logoImg = await loadImage(logoUrl);

  // Compute dimensions for slicing
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const pdfImgW = pageW - margin * 2;
  const scalePdf = pdfImgW / canvasWidth;

  // Compute line-height in canvas px
  const computed = window.getComputedStyle(element);
  const fontSizePx = parseFloat(computed.fontSize);
  const lineHeightRatio = parseFloat(computed.lineHeight);
  const lineHeightPx = fontSizePx * lineHeightRatio;
  const dpr = window.devicePixelRatio || 1;
  const lineHeightCanvasPx = lineHeightPx * dpr;

  // Determine how many canvas px fit per page (exact multiple of line-height)
  const usablePdfHeight = pageH - margin - headerY - 10;
  const usableCanvasHeight = usablePdfHeight / scalePdf;
  let sliceCanvasHeight = Math.floor(usableCanvasHeight / lineHeightCanvasPx) * lineHeightCanvasPx;
  if (sliceCanvasHeight < lineHeightCanvasPx) sliceCanvasHeight = usableCanvasHeight;

  // Draw watermark/header decorator
  const drawDecor = pageNum => {
    doc.setPage(pageNum);
    if (pageNum === 1) {
      doc.setFont('times', 'italic');
      doc.setFontSize(14);
      doc.setTextColor(60, 60, 60);
      doc.text('Vertikal Travel Planner', pageW / 2, headerY, { align: 'center' });
    }
    if (doc.setGState) {
      doc.setGState(new doc.GState({ opacity: 0.05 }));
      const wm = 100;
      doc.addImage(logoImg, 'PNG', (pageW - wm) / 2, (pageH - wm) / 2, wm, wm);
      doc.setGState(new doc.GState({ opacity: 1 }));
    }
  };

  // Paginate by cropping the canvas into non-overlapping slices
  let printed = 0;
  let pageNum = 1;
  while (printed < canvasHeight) {
    const currentHeight = Math.min(sliceCanvasHeight, canvasHeight - printed);
    // create slice
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvasWidth;
    sliceCanvas.height = currentHeight;
    const ctx = sliceCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, printed, canvasWidth, currentHeight, 0, 0, canvasWidth, currentHeight);
    const sliceData = sliceCanvas.toDataURL('image/png');
    const slicePdfHeight = currentHeight * scalePdf;

    // add to PDF slice
    doc.addImage(sliceData, 'PNG', margin, headerY + 10, pdfImgW, slicePdfHeight);
    drawDecor(pageNum);

    // advance
    printed += currentHeight;
    if (printed < canvasHeight) {
      doc.addPage();
      pageNum++;
    }
  }

  // Trigger download
  const blob = doc.output('blob');
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);

  // Restore original paragraph styles
  originalStyles.forEach(({ el, marginBottom, lineHeight }) => {
    el.style.marginBottom = marginBottom;
    el.style.lineHeight = lineHeight;
  });
}
